import { useCopilotAction } from '@copilotkit/react-core';

import { TrackableItem } from 'types/trackables';

import { useAPI } from 'hooks/api/useAPI';

// Helper to safely parse JSON or return the value if already parsed
function safeJsonParse<T>(value: string | T | undefined): T | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'string') return value as T;
  try {
    return JSON.parse(value) as T;
  } catch {
    // If it's a string that's not valid JSON, treat it as a single-item array
    return [value] as unknown as T;
  }
}

export function useListWatchlistItemsTool() {
  const { applicationID, client } = useAPI();

  useCopilotAction({
    name: 'listWatchlistItems',
    description:
      'List items within watchlists. Items are the individual faces, vehicles, or custom objects being tracked. Can search across all watchlists or within a specific one.',
    parameters: [
      {
        name: 'watchlistId',
        type: 'string',
        description:
          'ID of a specific watchlist to get items from. If not provided, searches across all watchlists.',
        required: false,
      },
      {
        name: 'watchlistIds',
        type: 'string',
        description:
          'JSON array of watchlist IDs to search across (used when watchlistId is not provided)',
        required: false,
      },
      {
        name: 'ids',
        type: 'string',
        description: 'JSON array of item IDs to fetch',
        required: false,
      },
      {
        name: 'search',
        type: 'string',
        description: 'Search term to filter items by name',
        required: false,
      },
      {
        name: 'labels',
        type: 'string',
        description: 'JSON array of labels to filter items by',
        required: false,
      },
      {
        name: 'limit',
        type: 'number',
        description: 'Maximum number of results to return. Defaults to 50.',
        required: false,
      },
    ],
    handler: async (args: {
      watchlistId?: string;
      watchlistIds?: string | string[];
      ids?: string | string[];
      search?: string;
      labels?: string | string[];
      limit?: number;
    }) => {
      try {
        const ids = safeJsonParse<string[]>(args.ids);
        const labels = safeJsonParse<string[]>(args.labels);
        const limit = args.limit || 50;

        // If a specific watchlist ID is provided, use the single-list endpoint
        if (args.watchlistId) {
          const { data } = await client.get(
            `/apps/${applicationID}/trackables/${args.watchlistId}/items`,
            {
              params: {
                pagination: 'offset',
                page: 1,
                limit,
                trackable_item_ids: ids,
                trackable_item_names: args.search ? [args.search] : undefined,
                labels,
              },
            }
          );

          const items = data?.data || [];

          if (items.length === 0) {
            return {
              message: 'No items found in this watchlist matching your criteria.',
              items: [],
            };
          }

          return {
            message: `Found ${items.length} item(s) in watchlist`,
            items: items.map((item: TrackableItem) => ({
              id: item.id,
              name: item.name,
              description: item.description,
              labels: item.labels,
              attributes: item.attributes,
              watchlistId: item.trackable_list_id,
              createdAt: item.created_at,
              updatedAt: item.updated_at,
            })),
          };
        }

        // Otherwise, search across multiple watchlists
        const watchlistIds = safeJsonParse<string[]>(args.watchlistIds);

        const { data } = await client.get(`/apps/${applicationID}/trackable_items`, {
          params: {
            pagination: 'cursor',
            limit,
            trackable_list_ids: watchlistIds,
            trackable_item_ids: ids,
            trackable_item_names: args.search ? [args.search] : undefined,
            labels,
          },
        });

        const items = data?.data || [];

        if (items.length === 0) {
          return {
            message: 'No watchlist items found matching your criteria.',
            items: [],
          };
        }

        return {
          message: `Found ${items.length} item(s) across watchlists`,
          items: items.map((item: TrackableItem) => ({
            id: item.id,
            name: item.name,
            description: item.description,
            labels: item.labels,
            attributes: item.attributes,
            watchlistId: item.trackable_list_id,
            createdAt: item.created_at,
            updatedAt: item.updated_at,
          })),
        };
      } catch (error) {
        return {
          message: 'Failed to load watchlist items',
          error: error instanceof Error ? error.message : 'Unknown error',
          items: [],
        };
      }
    },
  });
}
